import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, RefreshCw, Download, Sparkles } from 'lucide-react';
import ScrollReveal from '@/components/react-bits/scroll-reveal';
import { trackEvent } from '@/lib/analytics';

// ============================================
// TYPES
// ============================================
interface GeneratedMeme {
  id: string;
  imageUrl: string;
  prompt: string;
}

type GenState = 'idle' | 'generating' | 'success' | 'error';

// ============================================
// CONSTANTS
// ============================================
const EXAMPLE_PROMPTS = [
  'Honey Badger slapping the bear market',
  'Honey Badger eating a cobra on Wall Street',
  'Honey Badger HODL through the crash',
  'Honey Badger going to the moon',
];

const TYPING_PHRASES = [
  'slapping the bear market',
  'ignoring all the FUD',
  'eating cobras for breakfast',
  'going to the moon anyway',
];

// ============================================
// REAL API GENERATION (Vercel AI Gateway)
// Falls back to picsum in local dev without API key
// ============================================
async function generateMeme(prompt: string): Promise<GeneratedMeme> {
  const res = await fetch('/api/generate-meme', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt }),
  });

  if (!res.ok) throw new Error(`Generation failed: ${res.status}`);

  const data = (await res.json()) as { imageUrl: string; model: string };
  return {
    id: `meme-${Date.now()}`,
    imageUrl: data.imageUrl,
    prompt,
  };
}

// ============================================
// TYPING ANIMATION HOOK
// ============================================
function useTypingAnimation(phrases: string[], speed = 80) {
  const [text, setText] = useState('');
  const [phraseIdx, setPhraseIdx] = useState(0);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    let charIdx = 0;
    let deleting = false;
    const current = phrases[phraseIdx];

    const tick = () => {
      if (!deleting) {
        charIdx = Math.min(charIdx + 1, current.length);
        setText(current.slice(0, charIdx));
        if (charIdx === current.length) {
          deleting = true;
          timeout = setTimeout(tick, 1800);
        } else {
          timeout = setTimeout(tick, speed);
        }
      } else {
        charIdx = Math.max(charIdx - 1, 0);
        setText(current.slice(0, charIdx));
        if (charIdx === 0) {
          deleting = false;
          setPhraseIdx((p) => (p + 1) % phrases.length);
          timeout = setTimeout(tick, 300);
        } else {
          timeout = setTimeout(tick, speed / 2);
        }
      }
    };

    timeout = setTimeout(tick, 400);
    return () => clearTimeout(timeout);
  }, [phraseIdx, phrases, speed]);

  return text;
}

// ============================================
// COMPONENT
// ============================================
export default function MemeGenerator() {
  const [prompt, setPrompt] = useState('');
  const [genState, setGenState] = useState<GenState>('idle');
  const [memes, setMemes] = useState<GeneratedMeme[]>([]);
  const [currentMeme, setCurrentMeme] = useState<GeneratedMeme | null>(null);
  const typingText = useTypingAnimation(TYPING_PHRASES);

  const generate = useCallback(async () => {
    const finalPrompt = prompt.trim() || `Honey Badger ${typingText}`;
    if (!finalPrompt || genState === 'generating') return;
    trackEvent('meme_generate_click', { prompt_len: finalPrompt.length });
    setGenState('generating');
    try {
      const meme = await generateMeme(`Honey Badger ${finalPrompt}`);
      setCurrentMeme(meme);
      setMemes((prev) => [meme, ...prev].slice(0, 6));
      setGenState('success');
      trackEvent('meme_generate_success', { prompt_len: finalPrompt.length });
    } catch {
      setGenState('error');
      trackEvent('meme_generate_error');
      setTimeout(() => setGenState('idle'), 3000);
    }
  }, [prompt, genState, typingText]);

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void generate(); }
  };

  return (
    <section id="memes" className="relative section overflow-hidden">
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none"
        style={{
          width: 'min(680px, 82vw)',
          height: 8,
          background: 'linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.55) 50%, transparent 100%)',
          clipPath: 'polygon(18px 0%, calc(100% - 18px) 0%, 100% 100%, 0% 100%)',
        }}
      />
      {/* Top honey drip divider */}
      <div className="absolute top-0 left-0 right-0 pointer-events-none" style={{ lineHeight: 0 }}>
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="w-full" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,0 Q360,60 720,20 Q1080,0 1440,40 L1440,60 L0,60Z" fill="rgba(212,175,55,0.04)" />
        </svg>
      </div>

      <div className="section-content relative z-10">
        {/* Header */}
        <ScrollReveal>
          <p className="text-label text-center mb-4" style={{ color: '#D4AF37' }}>◆ &nbsp; Meme Lab &nbsp; ◆</p>
        </ScrollReveal>
        <ScrollReveal delay={0.1}>
          <h2 className="text-headline font-black text-center mb-4">
            <span style={{ color: '#FFFFFF' }}>Make Your </span>
            <span className="text-gold">Honey Badger</span>
          </h2>
        </ScrollReveal>
        <ScrollReveal delay={0.2}>
          <p className="text-body text-center mb-16" style={{ color: 'rgba(255,255,255,0.38)' }}>
            Generate a Honey Badger meme. Share it. Spread the chaos.
          </p>
        </ScrollReveal>

        {/* Generator */}
        <ScrollReveal delay={0.25}>
          <div className="max-w-2xl mx-auto">
            {/* Typing hint */}
            <div className="text-caption mb-3" style={{ color: '#666666' }}>
              Honey Badger is{' '}
              <span style={{ color: '#D4AF37' }}>{typingText}</span>
              <span className="animate-pulse" style={{ color: '#FFD700' }}>|</span>
            </div>

            {/* Input row */}
            <div
              className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-3 md:p-4"
              style={{
                border: '1px solid rgba(212,175,55,0.26)',
                background: 'linear-gradient(135deg, rgba(212,175,55,0.08) 0%, rgba(212,175,55,0.03) 60%, rgba(0,0,0,0.3) 100%)',
                clipPath: 'polygon(14px 0%, 100% 0%, calc(100% - 14px) 100%, 0% 100%)',
                boxShadow: '0 12px 36px rgba(0,0,0,0.34)',
              }}
            >
              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Describe your meme..."
                disabled={genState === 'generating'}
                className="flex-1 text-body text-white outline-none bg-transparent placeholder:text-white/20 disabled:opacity-50 min-w-0"
                style={{ caretColor: '#D4AF37' }}
              />
              <button
                onClick={() => void generate()}
                disabled={genState === 'generating'}
                className="flex items-center justify-center gap-2 px-5 py-3 text-caption font-semibold transition-all duration-200 disabled:opacity-40 sm:w-auto w-full"
                style={{
                  border: '1px solid rgba(212,175,55,0.35)',
                  color: '#D4AF37',
                  background: 'rgba(212,175,55,0.08)',
                  clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)',
                  minHeight: 44,
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(212,175,55,0.2)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(212,175,55,0.08)'; }}
              >
                {genState === 'generating' ? <RefreshCw size={13} className="animate-spin" /> : <Send size={13} />}
                {genState === 'generating' ? 'Generating...' : 'Generate'}
              </button>
            </div>

            {/* Example prompt pills */}
            <div className="flex flex-wrap gap-2 mt-4 mb-12">
              {EXAMPLE_PROMPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => { setPrompt(p); trackEvent('meme_prompt_suggest_click', { prompt: p }); }}
                  className="text-caption px-3 py-2 transition-all duration-200"
                  style={{ color: 'rgba(255,255,255,0.28)', border: '1px solid rgba(255,255,255,0.07)' }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLButtonElement;
                    el.style.color = '#D4AF37';
                    el.style.borderColor = 'rgba(212,175,55,0.35)';
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLButtonElement;
                    el.style.color = 'rgba(255,255,255,0.28)';
                    el.style.borderColor = 'rgba(255,255,255,0.07)';
                  }}
                  >
                    {p}
                  </button>
                ))}
            </div>

            {/* Result */}
            <AnimatePresence mode="wait">
              {genState === 'generating' && (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center py-20">
                  <Sparkles size={28} className="mb-4 animate-pulse" style={{ color: '#D4AF37' }} />
                  <p className="text-caption" style={{ color: '#666666' }}>Honey Badger is creating chaos...</p>
                </motion.div>
              )}

              {genState === 'success' && currentMeme && (
                <motion.div key="result" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
                  {/* Corner-bracket frame — no container background */}
                  <div
                    className="relative aspect-square w-full max-w-md mx-auto overflow-hidden"
                    style={{
                      clipPath: 'polygon(20px 0%, 100% 0%, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0% 100%, 0% 20px)',
                    }}
                  >
                    <img
                      src={currentMeme.imageUrl}
                      alt={currentMeme.prompt}
                      className="w-full h-full object-cover"
                      style={{ border: '1px solid rgba(212,175,55,0.15)' }}
                    />
                    {/* Corner brackets */}
                    {[['top-0 left-0', 'border-t-2 border-l-2'], ['top-0 right-0', 'border-t-2 border-r-2'], ['bottom-0 left-0', 'border-b-2 border-l-2'], ['bottom-0 right-0', 'border-b-2 border-r-2']].map(([pos, brd], i) => (
                      <div key={i} className={`absolute ${pos} w-5 h-5 ${brd}`} style={{ borderColor: '#D4AF37' }} />
                    ))}
                  </div>
                  <div className="flex justify-center mt-4">
                    <a
                      href={currentMeme.imageUrl}
                      download="honeybadger-meme.png"
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => trackEvent('meme_download_click')}
                      className="flex items-center gap-2 text-caption transition-colors"
                      style={{ color: '#555555' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = '#D4AF37'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = '#555555'; }}
                    >
                      <Download size={13} />
                      Download
                    </a>
                  </div>
                </motion.div>
              )}

              {genState === 'error' && (
                <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-12">
                  <p className="text-caption" style={{ color: 'rgba(255,80,80,0.8)' }}>Generation failed. Honey Badger tried though.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </ScrollReveal>

        {/* Gallery */}
        {memes.length > 1 && (
          <ScrollReveal delay={0.1}>
            <div className="mt-16">
              <p className="text-label text-center mb-8" style={{ color: '#444444' }}>Recent Memes</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                {memes.slice(1).map((meme) => (
                  <motion.div
                    key={meme.id}
                    className="aspect-square overflow-hidden cursor-pointer"
                    style={{ border: '1px solid rgba(212,175,55,0.08)' }}
                    onClick={() => { setCurrentMeme(meme); setGenState('success'); trackEvent('meme_gallery_select'); }}
                    whileHover={{ scale: 1.01 }}
                    transition={{ duration: 0.15 }}
                  >
                    <img src={meme.imageUrl} alt={meme.prompt} className="w-full h-full object-cover opacity-60 hover:opacity-100 transition-opacity duration-200" />
                  </motion.div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        )}
      </div>
    </section>
  );
}
